import React, { useState, useEffect } from 'react';
import { Modal, Button, Descriptions, Spin, Table, Card, Divider, Typography } from 'antd';
import { DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useGetPayslip, useDownloadPayslip } from '../../hooks/payroll/payslipQuery';
import { NAIRA } from '../../defaults/utilities';
import { formatCurrency } from '../../helpers/currency';

const { Title, Text } = Typography;

const PayslipModal = ({ visible, onCancel, userId, financialYearId, yearMonth, employeeName, yearValue, monthName }) => {
  const [payslipData, setPayslipData] = useState(null);
  const [loading, setLoading] = useState(false);

  const getPayslip = useGetPayslip();
  const downloadPayslip = useDownloadPayslip();

  const fetchPayslipData = async () => {
    if (userId && financialYearId && yearMonth) {
      setLoading(true);
      try {
        const data = await getPayslip(userId, financialYearId, yearMonth);
        setPayslipData(data);
      } catch (error) {
        console.error("Error fetching payslip:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // console.log(userId, financialYearId, yearMonth)
    if (visible) {
      fetchPayslipData();
    }
  }, [visible, userId, financialYearId, yearMonth]);

  const handleDownloadPayslip = async () => {
    try {
      await downloadPayslip(userId, financialYearId, yearMonth, employeeName);
    } catch (error) {
      console.error("Error downloading payslip:", error);
    }
  };

  const additionsColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `${NAIRA} ${formatCurrency(amount)}`
    }
  ];

  const deductionsColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `${NAIRA} ${formatCurrency(amount)}`
    }
  ];

  return (
    <Modal
      open={visible}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Payslip Information</span>
          <Button
            type="primary"
            onClick={handleDownloadPayslip}
            icon={<DownloadOutlined />}
            disabled={loading || !payslipData}
          >
            Download PDF
          </Button>
        </div>
      }
      onCancel={onCancel}
      width={800}
      footer={null}
    >
      <Spin spinning={loading}>
        {payslipData ? (
          <div style={{ marginTop: '20px' }}>
            <Card>
              <Title level={4} style={{ textAlign: 'center', marginBottom: '20px' }}>
                Payslip for {monthName} {yearValue}
              </Title>

              <Descriptions bordered column={2}>
                <Descriptions.Item label="Employee Name" span={2}>{payslipData.employeeName}</Descriptions.Item>
                <Descriptions.Item label="Staff ID">{payslipData.staffId}</Descriptions.Item>
                <Descriptions.Item label="Department">{payslipData.department}</Descriptions.Item>
                <Descriptions.Item label="Grade Level">{payslipData.gradeLevel}</Descriptions.Item>
                <Descriptions.Item label="Pay Point">{payslipData.payPoint}</Descriptions.Item>
                <Descriptions.Item label="Account Number">{payslipData.accountNumber}</Descriptions.Item>
                <Descriptions.Item label="Pay Period">{payslipData.payPeriod}</Descriptions.Item>
              </Descriptions>

              <Divider />

              <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
                <div style={{ flex: 1 }}>
                  <Text strong>Earnings</Text>
                  <Table
                    dataSource={payslipData.additions}
                    columns={additionsColumns}
                    pagination={false}
                    rowKey="description"
                    bordered
                    style={{ marginTop: '10px' }}
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <Text strong>Gross Pay</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1}>
                            <Text strong>{`${NAIRA} ${formatCurrency(payslipData.grossPay)}`}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <Text strong>Deductions</Text>
                  <Table
                    dataSource={payslipData.deductions}
                    columns={deductionsColumns}
                    pagination={false}
                    rowKey="description"
                    bordered
                    style={{ marginTop: '10px' }}
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <Text strong>Total Deductions</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1}>
                            <Text strong>{`${NAIRA} ${formatCurrency(payslipData.totalDeduction)}`}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </div>
              </div>

              <Card style={{ marginTop: '20px', backgroundColor: '#f5f5f5' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong>NET PAY:</Text>
                  <Text strong style={{ fontSize: '18px' }}>{`${NAIRA} ${formatCurrency(payslipData.netPay)}`}</Text>
                </div>
              </Card>
            </Card>
          </div>
        ) : !loading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <FilePdfOutlined style={{ fontSize: '48px', color: '#ccc' }} />
            <p>No payslip data available</p>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default PayslipModal;
